import React, { useState } from 'react';
import { Box, Typography, Tabs, Tab, Card, CardContent, Grid, Button, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, MenuItem, Checkbox, FormControlLabel, FormGroup, Divider, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, useTheme, useMediaQuery, Select, OutlinedInput, ListItemText } from '@mui/material';
import { Icon } from '@iconify/react';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
interface ReportingExportsProps {
  onLogout: () => void;
}
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}
function CustomTabPanel(props: TabPanelProps) {
  const {
    children,
    value,
    index,
    ...other
  } = props;
  return <div role="tabpanel" hidden={value !== index} id={`report-tabpanel-${index}`} aria-labelledby={`report-tab-${index}`} {...other} data-id="element-4754">
      {value === index && <Box sx={{
      py: 3
    }} data-id="element-4755">
          {children}
        </Box>}
    </div>;
}
function a11yProps(index: number) {
  return {
    id: `report-tab-${index}`,
    'aria-controls': `report-tabpanel-${index}`
  };
}
const ReportCard = ({
  icon,
  iconColor,
  title,
  dataPreview,
  lastRun,
  schedule,
  downloads,
  isRegulatory = false
}: any) => {
  return <Card sx={{
    backgroundColor: '#F3F5F7',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    boxShadow: 'none',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: '#FFFFFF',
      boxShadow: '0px 4px 12px rgba(0,0,0,0.08)'
    }
  }} data-id="element-4756">
      <CardContent sx={{
      p: '20px !important',
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column'
    }} data-id="element-4757">
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        mb: 2
      }} data-id="element-4758">
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 40,
          height: 40,
          borderRadius: '8px',
          backgroundColor: `${iconColor}15`,
          color: iconColor,
          mr: 2
        }} data-id="element-4759">
            <Icon icon={icon} width={24} height={24} data-id="element-4760" />
          </Box>
          <Typography sx={{
          fontSize: '14px',
          fontWeight: 700,
          color: '#1F2937'
        }} data-id="element-4761">
            {title}
          </Typography>
        </Box>

        <Box sx={{
        mb: 3,
        flexGrow: 1
      }} data-id="element-4762">
          {dataPreview.map((item: any, idx: number) => <Typography key={idx} sx={{
          fontSize: '12px',
          color: item.color || '#374151',
          fontWeight: item.bold ? 700 : 400,
          mb: 0.5,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5
        }} data-id="element-4763">
              {item.text}
            </Typography>)}
        </Box>

        <Box sx={{
        mt: 'auto'
      }} data-id="element-4764">
          {lastRun && <Typography sx={{
          fontSize: '12px',
          color: '#6B7280',
          mb: 1.5
        }} data-id="element-4765">
              Last Run: {lastRun}
            </Typography>}

          <Box sx={{
          display: 'flex',
          gap: 1,
          mb: schedule ? 1.5 : 0,
          flexWrap: 'wrap'
        }} data-id="element-4766">
            {downloads.map((dl: any, idx: number) => <Button key={idx} size="small" variant={dl.primary ? 'contained' : 'outlined'} sx={{
            fontSize: '10px',
            minWidth: 'auto',
            py: 0.5,
            px: 1,
            backgroundColor: dl.primary ? '#AE2A19' : isRegulatory ? '#1F2937' : '#F3F5F7',
            color: dl.primary ? '#FFFFFF' : isRegulatory ? '#FFFFFF' : '#0052CC',
            borderColor: dl.primary ? '#AE2A19' : isRegulatory ? '#1F2937' : '#E5E7EB',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: dl.primary ? '#8A2013' : isRegulatory ? '#111827' : '#E5E7EB',
              borderColor: dl.primary ? '#8A2013' : isRegulatory ? '#111827' : '#D1D5DB',
              boxShadow: 'none'
            }
          }} data-id="element-4767">
                {dl.label}
              </Button>)}
          </Box>

          {schedule && <Typography sx={{
          fontSize: '12px',
          color: '#6B7280'
        }} data-id="element-4768">
              Schedule: {schedule.text} |{' '}
              <Box component="span" sx={{
            color: '#0052CC',
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline'
            }
          }} data-id="element-4769">
                Edit
              </Box>
            </Typography>}
        </Box>
      </CardContent>
    </Card>;
};
export default function ReportingExports({
  onLogout
}: ReportingExportsProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [tabValue, setTabValue] = useState(0);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const handleSidebarToggle = () => setSidebarOpen(!sidebarOpen);
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => setTabValue(newValue);
  const corridors = ['Ghana-UK', 'Nigeria-US', 'Ghana-Senegal', 'Kenya-UK', 'South Africa-US'];
  const [selectedCorridors, setSelectedCorridors] = useState<string[]>(['Ghana-UK']);
  const scheduledReports = [{
    id: 1,
    name: 'Daily Transaction Summary',
    schedule: 'Daily at 00:00',
    recipients: 'treasury@ecobank.com',
    status: 'Active',
    lastRun: '2025-01-01 00:00 UTC',
    nextRun: '2025-01-02 00:00 UTC'
  }, {
    id: 2,
    name: 'Monthly Compliance Report',
    schedule: 'Monthly 1st at 09:00',
    recipients: 'compliance@ecobank.com, cfo@ecobank.com',
    status: 'Active',
    lastRun: '2024-12-01 09:00 UTC',
    nextRun: '2025-02-01 09:00 UTC'
  }, {
    id: 3,
    name: 'Weekly Corridor Performance',
    schedule: 'Weekly Mon 10:00',
    recipients: 'ops@ecobank.com',
    status: 'Active',
    lastRun: '2025-01-01 10:00 UTC',
    nextRun: '2025-01-06 10:00 UTC'
  }];
  return <Box sx={{
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: '#F8F9FA'
  }} data-id="element-4770">
      <TopBar onMenuClick={handleSidebarToggle} data-id="element-4771" />
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} activePage="reporting-exports" data-id="element-4772" />

      <Box component="main" sx={{
      flexGrow: 1,
      p: {
        xs: 2,
        md: 3
      },
      mt: 8,
      ml: 0,
      width: {
        sm: `calc(100% - 240px)`
      },
      transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen
      })
    }} data-id="element-4773">
        {/* Header */}
        <Box sx={{
        mb: 4
      }} data-id="element-4774">
          <Typography sx={{
          fontSize: '20px',
          fontWeight: 700,
          color: '#1F2937',
          mb: 0.5
        }} data-id="element-4775">
            Report Library
          </Typography>
          <Typography sx={{
          fontSize: '12px',
          color: '#6B7280'
        }} data-id="element-4776">
            Generate, schedule, and track compliance and operational reports
          </Typography>
        </Box>

        {/* Tabs */}
        <Box sx={{
        borderBottom: 1,
        borderColor: '#E5E7EB',
        mb: 3
      }} data-id="element-4777">
          <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" sx={{
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '14px',
            color: '#6B7280',
            minWidth: 'auto',
            mr: 4,
            px: 0,
            '&.Mui-selected': {
              color: '#1F2937'
            }
          },
          '& .MuiTabs-indicator': {
            backgroundColor: '#0052CC',
            height: '3px',
            borderTopLeftRadius: '3px',
            borderTopRightRadius: '3px'
          }
        }} data-id="element-4778">
            <Tab label="Operational" {...a11yProps(0)} data-id="element-4779" />
            <Tab label="Compliance" {...a11yProps(1)} data-id="element-4780" />
            <Tab label="Financial" {...a11yProps(2)} data-id="element-4781" />
            <Tab label="Regulatory" {...a11yProps(3)} data-id="element-4782" />
            <Tab label="Custom" {...a11yProps(4)} data-id="element-4783" />
          </Tabs>
        </Box>

        {/* Tab 1: Operational */}
        <CustomTabPanel value={tabValue} index={0} data-id="element-4784">
          <Grid container spacing={3} data-id="element-4785">
            <Grid item xs={12} md={6} lg={4} data-id="element-4786">
              <ReportCard icon="lucide:bar-chart-2" iconColor="#0052CC" title="Daily Transaction Summary" dataPreview={[{
              text: '1,247 Transactions'
            }, {
              text: '$4.5M Volume'
            }, {
              text: '98.3% Success Rate',
              color: '#0B7923'
            }]} lastRun="2025-01-01 23:45 UTC" schedule={{
              text: 'Daily at 00:00 UTC'
            }} downloads={[{
              label: 'PDF'
            }, {
              label: 'Excel'
            }, {
              label: 'CSV'
            }]} data-id="element-4787" />
            </Grid>
            <Grid item xs={12} md={6} lg={4} data-id="element-4788">
              <ReportCard icon="lucide:globe" iconColor="#0B7923" title="Corridor Performance" dataPreview={[{
              text: '32 Active Corridors'
            }, {
              text: '45 Failed (0.2% failure rate)'
            }, {
              text: 'Top: Ghana-UK (18% volume)'
            }]} lastRun="2025-01-01 22:00 UTC" schedule={{
              text: 'Weekly on Monday 09:00 UTC'
            }} downloads={[{
              label: 'PDF'
            }, {
              label: 'Excel'
            }, {
              label: 'CSV'
            }]} data-id="element-4789" />
            </Grid>
            <Grid item xs={12} md={6} lg={4} data-id="element-4790">
              <ReportCard icon="lucide:alert-triangle" iconColor="#AE2A19" title="Transaction Failure Analysis" dataPreview={[{
              text: '45 Failed Transactions'
            }, {
              text: 'Top Reason: Beneficiary Account Closed (28%)'
            }, {
              text: 'Recovery Rate: 75%',
              color: '#0B7923'
            }]} lastRun="2025-01-01 23:45 UTC" schedule={{
              text: 'Daily at 00:30 UTC'
            }} downloads={[{
              label: 'PDF'
            }, {
              label: 'Excel'
            }]} data-id="element-4791" />
            </Grid>
            <Grid item xs={12} md={6} lg={4} data-id="element-4792">
              <ReportCard icon="lucide:users" iconColor="#0052CC" title="Partner Activity Report" dataPreview={[{
              text: '78 Active Partners'
            }, {
              text: 'Top Partner: Bank Ghana (28% volume)'
            }, {
              text: '3 Partners Under Minimum Prefund',
              color: '#B38600'
            }]} lastRun="2025-01-01 23:00 UTC" schedule={{
              text: 'Weekly'
            }} downloads={[{
              label: 'PDF'
            }, {
              label: 'Excel'
            }]} data-id="element-4793" />
            </Grid>
          </Grid>
        </CustomTabPanel>

        {/* Tab 2: Compliance */}
        <CustomTabPanel value={tabValue} index={1} data-id="element-4794">
          <Grid container spacing={3} data-id="element-4795">
            <Grid item xs={12} md={6} lg={4} data-id="element-4796">
              <ReportCard icon="lucide:ban" iconColor="#AE2A19" title="Sanctions Match Log" dataPreview={[{
              text: '2 OFAC Hits',
              color: '#AE2A19'
            }, {
              text: '0 UN List Hits',
              color: '#0B7923'
            }, {
              text: '0 EU Sanctions',
              color: '#0B7923'
            }, {
              text: 'Status: All Investigated ✓',
              color: '#0B7923'
            }]} lastRun="2025-01-01 23:45 UTC" schedule={{
              text: 'Daily at 00:15 UTC'
            }} downloads={[{
              label: 'PDF'
            }, {
              label: 'Excel'
            }, {
              label: 'Export SAR/STR Files',
              primary: true
            }]} data-id="element-4797" />
            </Grid>
            <Grid item xs={12} md={6} lg={4} data-id="element-4798">
              <ReportCard icon="lucide:trending-up" iconColor="#B38600" title="AML Risk Summary" dataPreview={[{
              text: '147 Risk Scored Transactions'
            }, {
              text: 'High Risk: 3 (2.0%)',
              color: '#AE2A19'
            }, {
              text: 'Medium Risk: 12 (8.2%)',
              color: '#B38600'
            }, {
              text: 'Low Risk: 132 (89.8%)',
              color: '#0B7923'
            }]} lastRun="2025-01-01 23:30 UTC" downloads={[{
              label: 'PDF'
            }, {
              label: 'Excel'
            }]} data-id="element-4799" />
            </Grid>
            <Grid item xs={12} md={6} lg={4} data-id="element-4800">
              <ReportCard icon="lucide:calendar" iconColor="#B38600" title="Partner KYB Expiry Calendar" dataPreview={[{
              text: '4 KYB Due Soon (<30 days)',
              color: '#B38600'
            }, {
              text: '0 KYB Overdue',
              color: '#0B7923'
            }, {
              text: 'Renewal Alerts Sent: 4'
            }, {
              text: '• Bank Ghana Ltd - Expires 2025-01-15',
              color: '#B38600'
            }, {
              text: '• PSP Fintech - Expires 2025-01-22'
            }]} lastRun="2025-01-01 23:00 UTC" downloads={[{
              label: 'PDF'
            }]} data-id="element-4801" />
            </Grid>
            <Grid item xs={12} md={6} lg={4} data-id="element-4802">
              <ReportCard icon="lucide:clipboard-list" iconColor="#AE2A19" title="SAR/STR Submitted" dataPreview={[{
              text: '8 SARs Submitted YTD',
              color: '#AE2A19'
            }, {
              text: 'Latest: 2025-01-01'
            }, {
              text: 'FIU Receipt Confirmed: 7',
              color: '#0B7923'
            }, {
              text: 'Pending Response: 1',
              color: '#B38600'
            }]} downloads={[{
              label: 'PDF'
            }, {
              label: 'Excel'
            }]} data-id="element-4803" />
            </Grid>
          </Grid>
        </CustomTabPanel>

        {/* Tab 3: Financial */}
        <CustomTabPanel value={tabValue} index={2} data-id="element-4804">
          <Grid container spacing={3} data-id="element-4805">
            <Grid item xs={12} md={6} lg={4} data-id="element-4806">
              <ReportCard icon="lucide:arrow-left-right" iconColor="#0052CC" title="FX Conversion Summary" dataPreview={[{
              text: '47 Conversions'
            }, {
              text: '$125,430 Converted'
            }, {
              text: 'Margin Collected: $2,125',
              color: '#0B7923'
            }, {
              text: 'Gain/Loss: +$890',
              color: '#0B7923'
            }]} lastRun="2025-01-01 23:45 UTC" downloads={[{
              label: 'PDF'
            }, {
              label: 'Excel'
            }]} data-id="element-4807" />
            </Grid>
            <Grid item xs={12} md={6} lg={4} data-id="element-4808">
              <ReportCard icon="lucide:building-2" iconColor="#0B7923" title="Settlement Statement" dataPreview={[{
              text: '32 Corridors Settled'
            }, {
              text: '$4.2M Settled',
              color: '#0B7923'
            }, {
              text: '3 Corridors Pending',
              color: '#B38600'
            }, {
              text: 'Settlement Exceptions: 2',
              color: '#AE2A19'
            }]} downloads={[{
              label: 'By Partner [PDF]'
            }, {
              label: 'By Corridor [PDF]'
            }]} data-id="element-4809" />
            </Grid>
            <Grid item xs={12} md={6} lg={4} data-id="element-4810">
              <ReportCard icon="lucide:coins" iconColor="#0B7923" title="Billing & Fee Summary" dataPreview={[{
              text: 'Fees Earned: $12,450',
              color: '#0B7923'
            }, {
              text: 'VAT Collected: $2,745'
            }, {
              text: 'Partner Refunds: $150',
              color: '#AE2A19'
            }, {
              text: 'Net: $15,045',
              color: '#0B7923',
              bold: true
            }]} lastRun="2025-01-01 23:45 UTC" downloads={[{
              label: 'PDF'
            }, {
              label: 'Excel'
            }]} data-id="element-4811" />
            </Grid>
          </Grid>
        </CustomTabPanel>

        {/* Tab 4: Regulatory */}
        <CustomTabPanel value={tabValue} index={3} data-id="element-4812">
          <Grid container spacing={3} data-id="element-4813">
            <Grid item xs={12} md={6} lg={4} data-id="element-4814">
              <ReportCard icon="lucide:flag" iconColor="#1F2937" title="FIU Report - Ghana" dataPreview={[{
              text: '132 Transactions'
            }, {
              text: '$450K Volume'
            }, {
              text: 'SAR/STR Required: 0',
              color: '#0B7923'
            }, {
              text: 'Frequency: Monthly (auto-submitted)'
            }]} lastRun="2024-12-31 23:00 UTC" isRegulatory={true} downloads={[{
              label: 'Regulatory Format'
            }]} data-id="element-4815" />
            </Grid>
            <Grid item xs={12} md={6} lg={4} data-id="element-4816">
              <ReportCard icon="lucide:flag" iconColor="#1F2937" title="Central Bank Report - Nigeria" dataPreview={[{
              text: '45 Transactions'
            }, {
              text: '$125K Volume'
            }, {
              text: 'Cross-Border Payments Log'
            }]} lastRun="2024-12-31 23:00 UTC" isRegulatory={true} downloads={[{
              label: 'Regulatory Format'
            }]} data-id="element-4817" />
            </Grid>
            <Grid item xs={12} md={6} lg={4} data-id="element-4818">
              <ReportCard icon="lucide:pie-chart" iconColor="#0052CC" title="Group-wide Compliance Summary" dataPreview={[{
              text: 'All 33 Countries'
            }, {
              text: '1,247 Transactions'
            }, {
              text: '2 Regulatory Items',
              color: '#B38600'
            }, {
              text: '0 Overdue Actions',
              color: '#0B7923'
            }]} lastRun="2025-01-01 06:00 UTC" downloads={[{
              label: 'PDF'
            }, {
              label: 'Excel'
            }]} data-id="element-4819" />
            </Grid>
          </Grid>
        </CustomTabPanel>

        {/* Tab 5: Custom */}
        <CustomTabPanel value={tabValue} index={4} data-id="element-4820">
          <Box sx={{
          mb: 3
        }} data-id="element-4821">
            <Typography sx={{
            fontSize: '20px',
            fontWeight: 700,
            color: '#1F2937',
            mb: 0.5
          }} data-id="element-4822">
              Build Custom Report
            </Typography>
            <Typography sx={{
            fontSize: '12px',
            color: '#6B7280'
          }} data-id="element-4823">
              Create ad-hoc reports with custom filters and columns
            </Typography>
          </Box>

          <Box sx={{
          backgroundColor: '#F3F5F7',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          p: 3
        }} data-id="element-4824">
            <Grid container spacing={4} data-id="element-4825">
              {/* Left Column */}
              <Grid item xs={12} md={6} data-id="element-4826">
                <Box sx={{
                mb: 3
              }} data-id="element-4827">
                  <Typography sx={{
                  fontSize: '12px',
                  fontWeight: 700,
                  color: '#6B7280',
                  mb: 1
                }} data-id="element-4828">
                    Date Range *
                  </Typography>
                  <Box sx={{
                  display: 'flex',
                  gap: 2
                }} data-id="element-4829">
                    <TextField type="date" size="small" fullWidth defaultValue="2024-12-25" sx={{
                    backgroundColor: '#FFFFFF'
                  }} data-id="element-4830" />
                    <TextField type="date" size="small" fullWidth defaultValue="2025-01-01" sx={{
                    backgroundColor: '#FFFFFF'
                  }} data-id="element-4831" />
                  </Box>
                </Box>

                <Box sx={{
                mb: 3
              }} data-id="element-4832">
                  <Typography sx={{
                  fontSize: '12px',
                  fontWeight: 700,
                  color: '#6B7280',
                  mb: 1
                }} data-id="element-4833">
                    Corridor
                  </Typography>
                  <Select multiple displayEmpty value={selectedCorridors} onChange={e => setSelectedCorridors(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)} input={<OutlinedInput size="small" fullWidth sx={{
                  backgroundColor: '#FFFFFF'
                }} data-id="element-4835" />} renderValue={selected => {
                  if (selected.length === 0) return <em data-id="element-4836">All</em>;
                  return selected.join(', ');
                }} data-id="element-4834">
                    <MenuItem value="All" data-id="element-4837">
                      <Checkbox checked={selectedCorridors.indexOf('All') > -1} data-id="element-4838" />
                      <ListItemText primary="All" data-id="element-4839" />
                    </MenuItem>
                    {corridors.map(name => <MenuItem key={name} value={name} data-id="element-4840">
                        <Checkbox checked={selectedCorridors.indexOf(name) > -1} data-id="element-4841" />
                        <ListItemText primary={name} data-id="element-4842" />
                      </MenuItem>)}
                  </Select>
                </Box>

                <Box sx={{
                mb: 3
              }} data-id="element-4843">
                  <Typography sx={{
                  fontSize: '12px',
                  fontWeight: 700,
                  color: '#6B7280',
                  mb: 1
                }} data-id="element-4844">
                    Partner
                  </Typography>
                  <Select displayEmpty value="all" size="small" fullWidth sx={{
                  backgroundColor: '#FFFFFF'
                }} data-id="element-4845">
                    <MenuItem value="all" data-id="element-4846">All Partners</MenuItem>
                    <MenuItem value="bank-ghana" data-id="element-4847">Bank Ghana Ltd</MenuItem>
                    <MenuItem value="psp-fintech" data-id="element-4848">PSP Fintech</MenuItem>
                  </Select>
                </Box>

                <Box sx={{
                mb: 3
              }} data-id="element-4849">
                  <Typography sx={{
                  fontSize: '12px',
                  fontWeight: 700,
                  color: '#6B7280',
                  mb: 1
                }} data-id="element-4850">
                    Transaction Status
                  </Typography>
                  <FormGroup row data-id="element-4851">
                    <FormControlLabel control={<Checkbox defaultChecked size="small" data-id="element-4853" />} label={<Typography sx={{
                    fontSize: '14px'
                  }} data-id="element-4854">
                          Completed
                        </Typography>} data-id="element-4852" />
                    <FormControlLabel control={<Checkbox size="small" data-id="element-4856" />} label={<Typography sx={{
                    fontSize: '14px'
                  }} data-id="element-4857">
                          Processing
                        </Typography>} data-id="element-4855" />
                    <FormControlLabel control={<Checkbox size="small" data-id="element-4859" />} label={<Typography sx={{
                    fontSize: '14px'
                  }} data-id="element-4860">
                          Failed
                        </Typography>} data-id="element-4858" />
                    <FormControlLabel control={<Checkbox size="small" data-id="element-4862" />} label={<Typography sx={{
                    fontSize: '14px'
                  }} data-id="element-4863">
                          Blocked
                        </Typography>} data-id="element-4861" />
                  </FormGroup>
                </Box>
              </Grid>

              {/* Right Column */}
              <Grid item xs={12} md={6} data-id="element-4864">
                <Box sx={{
                mb: 3
              }} data-id="element-4865">
                  <Typography sx={{
                  fontSize: '12px',
                  fontWeight: 700,
                  color: '#6B7280',
                  mb: 1
                }} data-id="element-4866">
                    Risk Level
                  </Typography>
                  <Select displayEmpty value="all" size="small" fullWidth sx={{
                  backgroundColor: '#FFFFFF'
                }} data-id="element-4867">
                    <MenuItem value="all" data-id="element-4868">All</MenuItem>
                    <MenuItem value="high" data-id="element-4869">High</MenuItem>
                    <MenuItem value="medium" data-id="element-4870">Medium</MenuItem>
                    <MenuItem value="low" data-id="element-4871">Low</MenuItem>
                  </Select>
                </Box>

                <Box sx={{
                mb: 3
              }} data-id="element-4872">
                  <Typography sx={{
                  fontSize: '12px',
                  fontWeight: 700,
                  color: '#6B7280',
                  mb: 1
                }} data-id="element-4873">
                    Amount Range
                  </Typography>
                  <Box sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'center'
                }} data-id="element-4874">
                    <Typography sx={{
                    fontSize: '14px'
                  }} data-id="element-4875">
                      Min: $
                    </Typography>
                    <TextField size="small" fullWidth sx={{
                    backgroundColor: '#FFFFFF'
                  }} data-id="element-4876" />
                    <Typography sx={{
                    fontSize: '14px'
                  }} data-id="element-4877">
                      Max: $
                    </Typography>
                    <TextField size="small" fullWidth sx={{
                    backgroundColor: '#FFFFFF'
                  }} data-id="element-4878" />
                  </Box>
                </Box>

                <Box sx={{
                mb: 3
              }} data-id="element-4879">
                  <Typography sx={{
                  fontSize: '12px',
                  fontWeight: 700,
                  color: '#6B7280',
                  mb: 1
                }} data-id="element-4880">
                    Columns to Include
                  </Typography>
                  <Box sx={{
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E5E7EB',
                  borderRadius: '4px',
                  p: 1.5
                }} data-id="element-4881">
                    <Typography sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    mb: 0.5
                  }} data-id="element-4882">
                      Core
                    </Typography>
                    <FormGroup row sx={{
                    mb: 1
                  }} data-id="element-4883">
                      <FormControlLabel control={<Checkbox defaultChecked size="small" data-id="element-4885" />} label={<Typography sx={{
                      fontSize: '12px'
                    }} data-id="element-4886">
                            Transaction ID
                          </Typography>} data-id="element-4884" />
                      <FormControlLabel control={<Checkbox defaultChecked size="small" data-id="element-4888" />} label={<Typography sx={{
                      fontSize: '12px'
                    }} data-id="element-4889">
                            Partner
                          </Typography>} data-id="element-4887" />
                      <FormControlLabel control={<Checkbox defaultChecked size="small" data-id="element-4891" />} label={<Typography sx={{
                      fontSize: '12px'
                    }} data-id="element-4892">
                            Amount
                          </Typography>} data-id="element-4890" />
                      <FormControlLabel control={<Checkbox defaultChecked size="small" data-id="element-4894" />} label={<Typography sx={{
                      fontSize: '12px'
                    }} data-id="element-4895">
                            Corridor
                          </Typography>} data-id="element-4893" />
                      <FormControlLabel control={<Checkbox defaultChecked size="small" data-id="element-4897" />} label={<Typography sx={{
                      fontSize: '12px'
                    }} data-id="element-4898">
                            Status
                          </Typography>} data-id="element-4896" />
                    </FormGroup>
                    <Typography sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    mb: 0.5
                  }} data-id="element-4899">
                      Optional
                    </Typography>
                    <FormGroup row sx={{
                    mb: 1
                  }} data-id="element-4900">
                      <FormControlLabel control={<Checkbox size="small" data-id="element-4902" />} label={<Typography sx={{
                      fontSize: '12px'
                    }} data-id="element-4903">
                            Compliance Flag
                          </Typography>} data-id="element-4901" />
                      <FormControlLabel control={<Checkbox size="small" data-id="element-4905" />} label={<Typography sx={{
                      fontSize: '12px'
                    }} data-id="element-4906">
                            FX Rate
                          </Typography>} data-id="element-4904" />
                      <FormControlLabel control={<Checkbox size="small" data-id="element-4908" />} label={<Typography sx={{
                      fontSize: '12px'
                    }} data-id="element-4909">
                            Fees
                          </Typography>} data-id="element-4907" />
                    </FormGroup>
                  </Box>
                </Box>

                <Box sx={{
                display: 'flex',
                gap: 3
              }} data-id="element-4910">
                  <Box sx={{
                  flex: 1
                }} data-id="element-4911">
                    <Typography sx={{
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#6B7280',
                    mb: 1
                  }} data-id="element-4912">
                      Aggregation
                    </Typography>
                    <Select displayEmpty value="none" size="small" fullWidth sx={{
                    backgroundColor: '#FFFFFF'
                  }} data-id="element-4913">
                      <MenuItem value="none" data-id="element-4914">None (row-level)</MenuItem>
                      <MenuItem value="daily" data-id="element-4915">Daily</MenuItem>
                      <MenuItem value="weekly" data-id="element-4916">Weekly</MenuItem>
                      <MenuItem value="monthly" data-id="element-4917">Monthly</MenuItem>
                    </Select>
                  </Box>
                  <Box sx={{
                  flex: 1
                }} data-id="element-4918">
                    <Typography sx={{
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#6B7280',
                    mb: 1
                  }} data-id="element-4919">
                      Calculations
                    </Typography>
                    <FormGroup row data-id="element-4920">
                      <FormControlLabel control={<Checkbox size="small" data-id="element-4922" />} label={<Typography sx={{
                      fontSize: '12px'
                    }} data-id="element-4923">
                            Sum
                          </Typography>} data-id="element-4921" />
                      <FormControlLabel control={<Checkbox size="small" data-id="element-4925" />} label={<Typography sx={{
                      fontSize: '12px'
                    }} data-id="element-4926">
                            Average
                          </Typography>} data-id="element-4924" />
                    </FormGroup>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{
            my: 3
          }} data-id="element-4927" />

            <Box sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'flex-end'
          }} data-id="element-4928">
              <Button variant="outlined" sx={{
              color: '#1F2937',
              borderColor: '#E5E7EB',
              backgroundColor: '#FFFFFF',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#F8F9FA',
                borderColor: '#D1D5DB'
              }
            }} data-id="element-4929">
                Reset Filters
              </Button>
              <Button variant="outlined" sx={{
              color: '#1F2937',
              borderColor: '#E5E7EB',
              backgroundColor: '#FFFFFF',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#F8F9FA',
                borderColor: '#D1D5DB'
              }
            }} data-id="element-4930">
                Save as Template
              </Button>
              <Button variant="outlined" sx={{
              color: '#1F2937',
              borderColor: '#E5E7EB',
              backgroundColor: '#FFFFFF',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#F8F9FA',
                borderColor: '#D1D5DB'
              }
            }} data-id="element-4931">
                Preview Report
              </Button>
              <Button variant="contained" sx={{
              backgroundColor: '#0052CC',
              color: '#FFFFFF',
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#0043A8',
                boxShadow: 'none'
              }
            }} data-id="element-4932">
                Generate & Download
              </Button>
            </Box>
          </Box>
        </CustomTabPanel>

        {/* Scheduled Reports Section (Always Visible) */}
        <Box sx={{
        mt: 6
      }} data-id="element-4933">
          <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3
        }} data-id="element-4934">
            <Box data-id="element-4935">
              <Typography sx={{
              fontSize: '20px',
              fontWeight: 700,
              color: '#1F2937',
              mb: 0.5
            }} data-id="element-4936">
                Scheduled Reports
              </Typography>
              <Typography sx={{
              fontSize: '12px',
              color: '#6B7280'
            }} data-id="element-4937">
                Manage automated report generation and delivery
              </Typography>
            </Box>
            <Button variant="contained" onClick={() => setScheduleModalOpen(true)} sx={{
            backgroundColor: '#0052CC',
            color: '#FFFFFF',
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '14px',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#0043A8',
              boxShadow: 'none'
            }
          }} data-id="element-4938">
              Add New Scheduled Report
            </Button>
          </Box>

          <TableContainer component={Paper} sx={{
          boxShadow: 'none',
          border: '1px solid #E5E7EB',
          borderRadius: '8px'
        }} data-id="element-4939">
            <Table data-id="element-4940">
              <TableHead data-id="element-4941">
                <TableRow sx={{
                backgroundColor: '#F3F5F7'
              }} data-id="element-4942">
                  <TableCell sx={{
                  fontWeight: 700,
                  color: '#0052CC',
                  fontSize: '13px'
                }} data-id="element-4943">
                    Report Name
                  </TableCell>
                  <TableCell sx={{
                  fontWeight: 700,
                  color: '#0052CC',
                  fontSize: '13px'
                }} data-id="element-4944">
                    Schedule
                  </TableCell>
                  <TableCell sx={{
                  fontWeight: 700,
                  color: '#0052CC',
                  fontSize: '13px'
                }} data-id="element-4945">
                    Recipient Email(s)
                  </TableCell>
                  <TableCell sx={{
                  fontWeight: 700,
                  color: '#0052CC',
                  fontSize: '13px'
                }} data-id="element-4946">
                    Status
                  </TableCell>
                  <TableCell sx={{
                  fontWeight: 700,
                  color: '#0052CC',
                  fontSize: '13px'
                }} data-id="element-4947">
                    Last Run
                  </TableCell>
                  <TableCell sx={{
                  fontWeight: 700,
                  color: '#0052CC',
                  fontSize: '13px'
                }} data-id="element-4948">
                    Next Run
                  </TableCell>
                  <TableCell sx={{
                  fontWeight: 700,
                  color: '#0052CC',
                  fontSize: '13px'
                }} data-id="element-4949">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody data-id="element-4950">
                {scheduledReports.map(row => <TableRow key={row.id} sx={{
                '&:last-child td, &:last-child th': {
                  border: 0
                }
              }} data-id="element-4951">
                    <TableCell sx={{
                  fontSize: '13px',
                  color: '#1F2937',
                  fontWeight: 500
                }} data-id="element-4952">
                      {row.name}
                    </TableCell>
                    <TableCell sx={{
                  fontSize: '13px',
                  color: '#374151'
                }} data-id="element-4953">
                      {row.schedule}
                    </TableCell>
                    <TableCell sx={{
                  fontSize: '13px',
                  color: '#374151'
                }} data-id="element-4954">
                      {row.recipients}
                    </TableCell>
                    <TableCell data-id="element-4955">
                      <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }} data-id="element-4956">
                        <Box sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: '#0B7923'
                    }} data-id="element-4957" />
                        <Typography sx={{
                      fontSize: '13px',
                      color: '#374151'
                    }} data-id="element-4958">
                          {row.status}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{
                  fontSize: '13px',
                  color: '#6B7280'
                }} data-id="element-4959">
                      {row.lastRun}
                    </TableCell>
                    <TableCell sx={{
                  fontSize: '13px',
                  color: '#6B7280'
                }} data-id="element-4960">
                      {row.nextRun}
                    </TableCell>
                    <TableCell data-id="element-4961">
                      <Box sx={{
                    display: 'flex',
                    gap: 1
                  }} data-id="element-4962">
                        <Typography sx={{
                      fontSize: '13px',
                      color: '#0052CC',
                      cursor: 'pointer',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }} data-id="element-4963">
                          Edit
                        </Typography>
                        <Typography sx={{
                      fontSize: '13px',
                      color: '#6B7280',
                      cursor: 'pointer',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }} data-id="element-4964">
                          Pause
                        </Typography>
                        <Typography sx={{
                      fontSize: '13px',
                      color: '#AE2A19',
                      cursor: 'pointer',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }} data-id="element-4965">
                          Delete
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 2
        }} data-id="element-4966">
            <Typography sx={{
            fontSize: '13px',
            color: '#6B7280'
          }} data-id="element-4967">
              Showing 1-3 of 23 scheduled reports
            </Typography>
            <Box sx={{
            display: 'flex',
            gap: 2
          }} data-id="element-4968">
              <Typography sx={{
              fontSize: '13px',
              color: '#6B7280',
              cursor: 'pointer'
            }} data-id="element-4969">
                &lt; Previous
              </Typography>
              <Typography sx={{
              fontSize: '13px',
              color: '#0052CC',
              cursor: 'pointer'
            }} data-id="element-4970">
                Next &gt;
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Add Schedule Modal */}
      <Dialog open={scheduleModalOpen} onClose={() => setScheduleModalOpen(false)} maxWidth="sm" fullWidth data-id="element-4971">
        <DialogTitle sx={{
        fontSize: '18px',
        fontWeight: 700,
        color: '#1F2937',
        pb: 1
      }} data-id="element-4972">
          Add New Scheduled Report
        </DialogTitle>
        <DialogContent data-id="element-4973">
          <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          pt: 1
        }} data-id="element-4974">
            <Box data-id="element-4975">
              <Typography sx={{
              fontSize: '13px',
              fontWeight: 600,
              color: '#374151',
              mb: 1
            }} data-id="element-4976">
                Report Template
              </Typography>
              <Select fullWidth size="small" defaultValue="daily-summary" data-id="element-4977">
                <MenuItem value="daily-summary" data-id="element-4978">
                  Daily Transaction Summary
                </MenuItem>
                <MenuItem value="aml-risk" data-id="element-4979">AML Risk Summary</MenuItem>
                <MenuItem value="corridor-perf" data-id="element-4980">Corridor Performance</MenuItem>
              </Select>
            </Box>
            <Box sx={{
            display: 'flex',
            gap: 2
          }} data-id="element-4981">
              <Box sx={{
              flex: 1
            }} data-id="element-4982">
                <Typography sx={{
                fontSize: '13px',
                fontWeight: 600,
                color: '#374151',
                mb: 1
              }} data-id="element-4983">
                  Frequency
                </Typography>
                <Select fullWidth size="small" defaultValue="daily" data-id="element-4984">
                  <MenuItem value="daily" data-id="element-4985">Daily</MenuItem>
                  <MenuItem value="weekly" data-id="element-4986">Weekly</MenuItem>
                  <MenuItem value="monthly" data-id="element-4987">Monthly</MenuItem>
                </Select>
              </Box>
              <Box sx={{
              flex: 1
            }} data-id="element-4988">
                <Typography sx={{
                fontSize: '13px',
                fontWeight: 600,
                color: '#374151',
                mb: 1
              }} data-id="element-4989">
                  Time (UTC)
                </Typography>
                <TextField type="time" fullWidth size="small" defaultValue="00:00" data-id="element-4990" />
              </Box>
            </Box>
            <Box data-id="element-4991">
              <Typography sx={{
              fontSize: '13px',
              fontWeight: 600,
              color: '#374151',
              mb: 1
            }} data-id="element-4992">
                Email Recipients
              </Typography>
              <TextField fullWidth size="small" placeholder="Enter email addresses separated by comma" data-id="element-4993" />
            </Box>
            <FormControlLabel control={<Checkbox defaultChecked size="small" data-id="element-4995" />} label={<Typography sx={{
            fontSize: '14px'
          }} data-id="element-4996">
                  Notify when complete
                </Typography>} data-id="element-4994" />
          </Box>
        </DialogContent>
        <DialogActions sx={{
        p: 3,
        pt: 0
      }} data-id="element-4997">
          <Button onClick={() => setScheduleModalOpen(false)} sx={{
          color: '#6B7280',
          textTransform: 'none'
        }} data-id="element-4998">
            Cancel
          </Button>
          <Button variant="contained" onClick={() => setScheduleModalOpen(false)} sx={{
          backgroundColor: '#0052CC',
          color: '#FFFFFF',
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: '#0043A8',
            boxShadow: 'none'
          }
        }} data-id="element-4999">
            Save Schedule
          </Button>
        </DialogActions>
      </Dialog>
    </Box>;
}